import { useCallback, useEffect, useMemo, useRef, useState } from '@wordpress/element';
import CoachInteractionAdapter from '../adapters/coach/coachInteractionAdapter.js';
import { postCoachInteraction } from '../api/clientApi.js';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function makeAnswerKey(packId, moduleId, trackId, stepId) {
  const p = String(packId || '').trim();
  const m = String(moduleId || '').trim();
  const t = String(trackId || '').trim();
  const s = String(stepId || '').trim();

  if (!p || !m || !t || !s) {
    return s;
  }

  return `${p}::${m}::${t}::${s}`;
}

function buildResponses({ answers, steps, packId, moduleId, trackId }) {
  const responses = {};
  const safeAnswers = asObject(answers);

  for (const step of asArray(steps)) {
    const stepId = asString(step?.id).trim();

    if (!stepId) {
      continue;
    }

    const answerKey = makeAnswerKey(packId, moduleId, trackId, stepId);

    if (Object.prototype.hasOwnProperty.call(safeAnswers, answerKey)) {
      responses[stepId] = safeAnswers[answerKey];
      continue;
    }

    if (Object.prototype.hasOwnProperty.call(safeAnswers, stepId)) {
      responses[stepId] = safeAnswers[stepId];
    }
  }

  return responses;
}

function appendPathItem(path, choiceId) {
  const safePath = asArray(path)
    .map((item) => asString(item).trim())
    .filter(Boolean);

  const safeChoiceId = asString(choiceId).trim();

  if (!safeChoiceId) {
    return safePath;
  }

  return [...safePath, safeChoiceId];
}

export default function useCoachInteractionFlow({
  dispatch,
  activeStepId = '',
  packId = '',
  moduleId = '',
  trackId = '',
  steps = [],
  answers = {},
  selectedCoachId = 'system',
  selectedTypingMode = 'fast',
  baseEntries = [],
  onError = null,
}) {
  const previousStepIdRef = useRef('');

  const [interactionEntries, setInteractionEntries] = useState([]);
  const [interactionPath, setInteractionPath] = useState([]);
  const [hiddenChoiceBlockIds, setHiddenChoiceBlockIds] = useState([]);
  const [closedFeedbackIds, setClosedFeedbackIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const safeStepId = asString(activeStepId).trim();

  useEffect(() => {
    if (!safeStepId) {
      return;
    }

    if (previousStepIdRef.current === safeStepId) {
      return;
    }

    previousStepIdRef.current = safeStepId;

    setInteractionEntries([]);
    setInteractionPath([]);
    setHiddenChoiceBlockIds([]);
    setClosedFeedbackIds([]);
    setIsLoading(false);
  }, [safeStepId]);

  const isInteractionActive = interactionEntries.length > 0;

  const displayEntries = useMemo(() => {
    if (isInteractionActive) {
      return asArray(interactionEntries);
    }

    return asArray(baseEntries);
  }, [baseEntries, interactionEntries, isInteractionActive]);

  const handleChoiceSelect = useCallback(
    async ({ choiceId, blockId }) => {
      const safeChoiceId = asString(choiceId).trim();
      const safeBlockId = asString(blockId).trim();
      const safeModuleId = asString(moduleId).trim();

      if (!safeChoiceId || !safeModuleId || !safeStepId) {
        return;
      }

      const nextInteractionPath = appendPathItem(interactionPath, safeChoiceId);

      if (safeBlockId) {
        setHiddenChoiceBlockIds((current) => {
          if (current.includes(safeBlockId)) {
            return current;
          }

          return [...current, safeBlockId];
        });
      }

      const responses = buildResponses({
        answers,
        steps,
        packId,
        moduleId: safeModuleId,
        trackId,
      });

      setIsLoading(true);

      try {
        const response = await postCoachInteraction({
          payload: {
            pack_id: packId,
            module_id: safeModuleId,
            track: trackId,
            responses,
            current_step_id: safeStepId,
            assistant: {
              id: selectedCoachId,
            },
            typing_mode: selectedTypingMode,
            interaction: {
              choice_id: safeChoiceId,
              path: nextInteractionPath,
            },
          },
        });

        const adapted = CoachInteractionAdapter(response);
        const nextInteractionEntries = asArray(adapted?.entries);

        if (!nextInteractionEntries.length) {
          return;
        }

        setInteractionPath(nextInteractionPath);
        setInteractionEntries(nextInteractionEntries);
      } catch (error) {
        if (typeof onError === 'function') {
          onError(error);
        }

        if (dispatch) {
          dispatch({
            type: 'SET_ERROR',
            payload: {
              error: error?.message || 'Failed to load coach interaction.',
            },
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      answers,
      dispatch,
      interactionPath,
      moduleId,
      onError,
      packId,
      safeStepId,
      selectedCoachId,
      selectedTypingMode,
      steps,
      trackId,
    ]
  );

  const handleFeedback = useCallback(({ entryId }) => {
    const safeEntryId = asString(entryId).trim();

    if (!safeEntryId) {
      return;
    }

    setClosedFeedbackIds((current) => {
      if (current.includes(safeEntryId)) {
        return current;
      }

      return [...current, safeEntryId];
    });
  }, []);

  const handleRestart = useCallback(() => {
    setInteractionEntries([]);
    setInteractionPath([]);
    setHiddenChoiceBlockIds([]);
    setClosedFeedbackIds([]);
    setIsLoading(false);
  }, []);

  return {
    displayEntries,
    interactionEntries,
    interactionPath,
    isInteractionActive,
    hiddenChoiceBlockIds,
    closedFeedbackIds,
    isLoading,
    handleChoiceSelect,
    handleFeedback,
    handleRestart,
  };
}
